import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageBody } from '../../../../components/page-components/PageBody';
import { Table } from '../../../../components/tables/Table';
import { navigateToPage } from '../../../../utils/history';
import { InventorySvc } from '../../../../shared/services/inventory-service';
import { createResolveConflictsPayload } from '../../../../modules/inventory/models/resolve-conflicts-payload';
import { PhysicalInventoryConflicts, HorizontalPhysicalInventoryConflictItem, HorizontalPhysicalInventoryConflictEntry, VerticalPhysicalInventoryConflictItem, PhysicalInventoryConflictItemBase } from '../../../../modules/inventory/models/physical-inventory-conflicts';
import { PhysicalControlGroupLayout } from '../../../../modules/inventory/models/physical-inventory-details';
import { StatusCode } from '../../../../utils/api';
import { getQuantity } from '../../../../modules/inventory/models/utils';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';

interface IProps {
    location: any;
}

export const InventoryPhysicalResolveConflictsScene = React.forwardRef((props: IProps, ref) => {

    const [service] = React.useState(new InventorySvc());
    const [conflicts, setConflicts] = React.useState(new PhysicalInventoryConflicts());

    function getReportId() {
        const opts = props.location.pathname.split('/');
        return opts.length > 4 ? opts[4] : null;
    }

    function getControlNumber() {
        const opts = props.location.pathname.split('/');
        return opts.length > 6 ? opts[6] : null;
    }

    const getBottomButtons = () => {

        return <HasPermission requiredPermissions={[
            Permissions.InventoryPhysicalControlGroupsEdit
        ]}>
            <button className="btn complete" onClick={() => resolveAll()}>
                RESOLVE ALL
            </button>
        </HasPermission>;
    }

    function getVerticalEntry(conflict: VerticalPhysicalInventoryConflictItem, entryNumber: number) {
        if (entryNumber == 1) {
            return conflict.entry1;
        }
        else if (entryNumber == 2) {
            return conflict.entry2;
        }
        else {
            return null;
        }
    }

    function getHorizontalEntry(conflict: HorizontalPhysicalInventoryConflictItem, entryNumber: number) {
        if (entryNumber == 1) {
            return conflict.entry1;
        }
        else if (entryNumber == 2) {
            return conflict.entry2;
        }
        else {
            return null;
        }
    }

    function goBack() {
        var reportId = getReportId();
        navigateToPage(Routes.INVENTORY_PHYSICAL_DETAILS_CONTROLGROUPS.formattify(reportId));
    }

    async function getConflicts(reportId: string, controlNumber: string) {

        if (reportId) {
            const result = await service.getConflicts(reportId, controlNumber);
            if (result.status === StatusCode.Success) {

                var conflicts = new PhysicalInventoryConflicts(result.data);

                setConflicts(conflicts);
            }
        }
        else {
            goBack();
        }
    }

    function handleHorizontalBlur(e: React.ChangeEvent<HTMLInputElement>, idx: number, entryNumber: number) {
        var tmpConflicts = { ...conflicts };
        var conflict = conflicts.conflicts[idx] as HorizontalPhysicalInventoryConflictItem;

        var entry: HorizontalPhysicalInventoryConflictEntry;
        if (entryNumber == 1) {
            entry = conflict.entry1;
        }
        else if (entryNumber == 2) {
            entry = conflict.entry2;
        }
        else {
            return;
        }

        if (e?.target) {
            if (e.target.name == "quantity1") {
                entry.quantity1 = getQuantity(entry.quantity1);
            }
            if (e.target.name == "quantity2") {
                entry.quantity2 = getQuantity(entry.quantity2);
            }
            if (e.target.name == "quantity3") {
                entry.quantity3 = getQuantity(entry.quantity3);
            }
            if (e.target.name == "quantity4") {
                entry.quantity4 = getQuantity(entry.quantity4);
            }
            if (e.target.name == "quantity5") {
                entry.quantity5 = getQuantity(entry.quantity5);
            }
            if (e.target.name == "quantity6") {
                entry.quantity6 = getQuantity(entry.quantity6);
            }

            entry.total = entry.getTotal();
        }

        setConflicts(tmpConflicts);
    }

    function handleHorizontalChange(e: React.ChangeEvent<HTMLInputElement>, idx: number, entryNumber: number) {
        var tmpConflicts = { ...conflicts };
        var conflict = conflicts.conflicts[idx] as HorizontalPhysicalInventoryConflictItem;

        var entry: HorizontalPhysicalInventoryConflictEntry;
        if (entryNumber == 1) {
            entry = conflict.entry1;
        }
        else if (entryNumber == 2) {
            entry = conflict.entry2;
        }
        else {
            return;
        }

        if (e?.target) {
            if (e.target.name == "quantity1") {
                entry.quantity1 = e.target.value;
            }
            if (e.target.name == "quantity2") {
                entry.quantity2 = e.target.value;
            }
            if (e.target.name == "quantity3") {
                entry.quantity3 = e.target.value;
            }
            if (e.target.name == "quantity4") {
                entry.quantity4 = e.target.value;
            }
            if (e.target.name == "quantity5") {
                entry.quantity5 = e.target.value;
            }
            if (e.target.name == "quantity6") {
                entry.quantity6 = e.target.value;
            }
        }

        setConflicts(tmpConflicts);
    }

    function handleVerticalBlur(e: React.ChangeEvent<HTMLInputElement>, conflictIdx: number, entryNumber: number, qtyIdx: number) {
        var tmpConflicts = { ...conflicts };
        var conflict = conflicts.conflicts[conflictIdx] as VerticalPhysicalInventoryConflictItem;

        var entry = getVerticalEntry(conflict, entryNumber);

        if (entry != null) {
            if (e?.target && entry.quantities.length > qtyIdx) {
                if (e.target.name == "quantity1") {
                    entry.quantities[qtyIdx].quantity1 = getQuantity(entry.quantities[qtyIdx].quantity1);
                }
                if (e.target.name == "quantity2") {
                    entry.quantities[qtyIdx].quantity2 = getQuantity(entry.quantities[qtyIdx].quantity2);
                }

                entry.total = entry.getTotal();
            }

            setConflicts(tmpConflicts);
        }
    }

    function handleVerticalChange(e: React.ChangeEvent<HTMLInputElement>, conflictIdx: number, entryNumber: number, qtyIdx: number) {
        var tmpConflicts = { ...conflicts };
        var conflict = conflicts.conflicts[conflictIdx] as VerticalPhysicalInventoryConflictItem;

        var entry = getVerticalEntry(conflict, entryNumber);

        if (entry != null) {
            if (e?.target && entry.quantities.length > qtyIdx) {
                if (e.target.name == "quantity1") {
                    entry.quantities[qtyIdx].quantity1 = e.target.value;
                }
                if (e.target.name == "quantity2") {
                    entry.quantities[qtyIdx].quantity2 = e.target.value;
                }
            }

            setConflicts(tmpConflicts);
        }
    }

    function resolveHorizontal(item: HorizontalPhysicalInventoryConflictItem) {
        const payload = createResolveConflictsPayload(getReportId(), item.controlNumber, [item]);

        service.resolveConflicts(payload).then(result => {
            hideConflict(item);
        });
    }

    function resolveVertical(item: VerticalPhysicalInventoryConflictItem) {
        const payload = createResolveConflictsPayload(getReportId(), item.controlNumber, [item]);

        service.resolveConflicts(payload).then(result => {
            hideConflict(item);
        });
    }

    function resolveAll() {
        var tmpConflicts = { ...conflicts };

        // only the conflicts that have been made to match can be resolved
        const resolvable = tmpConflicts.conflicts.filter(item => {
            if (item.layout == PhysicalControlGroupLayout.horizontal) {
                const hItem = item as HorizontalPhysicalInventoryConflictItem;
                return hItem.entry1.total == hItem.entry2.total;
            }
            if (item.layout == PhysicalControlGroupLayout.vertical) {
                const vItem = item as VerticalPhysicalInventoryConflictItem;
                return vItem.entry1.total == vItem.entry2.total;
            }
            return false;
        }) as (HorizontalPhysicalInventoryConflictItem | VerticalPhysicalInventoryConflictItem)[];

        const payload = createResolveConflictsPayload(getReportId(), getControlNumber(), resolvable);

        service.resolveConflicts(payload).then(result => {
            getConflicts(getReportId(), getControlNumber()); 
        });
    }

    function hideConflict(item: PhysicalInventoryConflictItemBase) {
        var tmpConflicts = { ...conflicts };
        for (let tmpItem of tmpConflicts.conflicts) {
            if (tmpItem.partNumber === item.partNumber) {
                tmpItem.isResolved = true;
            }
        }
        setConflicts(tmpConflicts);
    }

    React.useEffect(() => {
        getConflicts(getReportId(), getControlNumber());
    }, [props.location]);

    return <PageContent pageName={PageNames.INVENTORY_PHYSICAL_CONTROL_GROUP_ENTRY} classes='inventory-physical'>
        <PageBody>
            <div className="row">
                <div className="col-1">
                </div>
                <div className="col-2">
                    <span className="invnetory-label">Control ID</span>
                    <span className="invnetory-value">{getControlNumber()}</span>
                </div>
                <div className="col-4">
                    <span className="invnetory-label">REPORT DATE</span>
                    <span className="invnetory-value">{conflicts.reportDate}</span>
                </div>
                <div className="col-4">
                    <span className="invnetory-label">CONFLICTS REMAINING</span>
                    <span className="invnetory-value conflicts">{conflicts.conflictsRemaining}</span>
                </div>
                <div className="col-1">
                </div>
            </div>
            {
                conflicts.conflicts.map((itm, conflictIdx) => (

                    itm.isResolved ? null :
                    <div className="row">
                        <div className="col">
                            {
                                itm.layout == PhysicalControlGroupLayout.horizontal
                                    ? <div className="horizontal-conflict">
                                        <div className="horizontal-conflict-data">
                                            <Table tableClass="horizontal-conflict-header"
                                                header={[
                                                    'CONTROL ID',
                                                    'SERVICE CENTER',
                                                    'CONTROL NAME',
                                                    'PART #',
                                                    'TYPE',
                                                    'SUB TYPE',
                                                    'UNIT',
                                                    'DESCRIPTION',
                                                    'LINE #'
                                                ]}
                                                noResultsMessage="">
                                                <tr>
                                                    <td><span>{itm.controlNumber}</span></td>
                                                    <td><span>{itm.serviceCenter}</span></td>
                                                    <td><span>{itm.controlName}</span></td>
                                                    <td><span>{itm.partNumber}</span></td>
                                                    <td><span>{itm.type}</span></td>
                                                    <td><span>{itm.subType}</span></td>
                                                    <td><span>{itm.unitOfMeasure}</span></td>
                                                    <td><span>{itm.description}</span></td>
                                                    <td><span>{itm.lineNumber}</span></td>
                                                </tr>
                                            </Table>

                                            {
                                                [1, 2].map((entryNumber) => (
                                                    <Table tableClass="horizontal-conflict-body"
                                                        header={[
                                                            '',
                                                            'QTY 1',
                                                            'QTY 2',
                                                            'QTY 3',
                                                            'QTY 4',
                                                            'QTY 5',
                                                            'QTY 6',
                                                            'TOTAL'
                                                        ]}
                                                        noResultsMessage="No conflicts found">
                                                        <tr>
                                                            <th style={{ width: '16%' }}><span>Entry {entryNumber} data</span></th>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity1"
                                                                    className="form-control"
                                                                                                                                        name="quantity1"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity1}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity2"
                                                                    className="form-control"
                                                                    name="quantity2"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity2}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity3"
                                                                    className="form-control"
                                                                    name="quantity3"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity3}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity4"
                                                                    className="form-control"
                                                                    name="quantity4"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity4}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity5"
                                                                    className="form-control"
                                                                    name="quantity5"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity5}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}>
                                                                <input
                                                                    id="quantity6"
                                                                    className="form-control"
                                                                    name="quantity6"
                                                                    value={getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.quantity6}
                                                                    min="0"
                                                                    onChange={(e) => handleHorizontalChange(e, conflictIdx, entryNumber)}
                                                                    onBlur={(e) => handleHorizontalBlur(e, conflictIdx, entryNumber)}
                                                                />
                                                            </td>
                                                            <td style={{ width: '12%' }}><span className="conflict-total">{getHorizontalEntry(itm as HorizontalPhysicalInventoryConflictItem, entryNumber)?.total}</span></td>
                                                        </tr>
                                                    </Table>
                                                ))
                                            }
                                        </div>
                                        <div className="horizontal-conflict-actions">
                                            <button className="btn new attention"
                                                onClick={e => resolveHorizontal(itm as HorizontalPhysicalInventoryConflictItem)}
                                                disabled={(itm as HorizontalPhysicalInventoryConflictItem).entry1?.total != (itm as HorizontalPhysicalInventoryConflictItem).entry2?.total}
                                            >RESOLVE AND SAVE</button>
                                        </div>
                                    </div>
                                    : <div className="vertical-conflict">
                                        <div className="vertical-conflict-header">
                                            <Table tableClass=""
                                                header={[
                                                    'CONTROL ID',
                                                    'SERVICE CENTER',
                                                    'CONTROL NAME',
                                                    'PART #',
                                                    'COLUMN #',
                                                ]}
                                                noResultsMessage="">
                                                <tr>
                                                    <td><span>{itm.controlNumber}</span></td>
                                                    <td><span>{itm.serviceCenter}</span></td>
                                                    <td><span>{itm.controlName}</span></td>
                                                    <td><span>{itm.partNumber}</span></td>
                                                    <td><span>{itm.columnNumber}</span></td>
                                                </tr>
                                            </Table>
                                            <div><span>{itm.description}</span></div>
                                        </div>
                                        <div className="vertical-conflict-data">
                                            {
                                                [1, 2].map((entryNumber) => (
                                                    <div className="vertical-conflict-entry">
                                                        <div className="vertical-conflict-section">
                                                            <span className="invnetory-label">ENTRY {entryNumber} DATA</span>
                                                        </div>
                                                        <Table 
                                                            header={["", "COLUMN 1", "COLUMN 2"]}
                                                            noResultsMessage="No conflicts found">
                                                            {
                                                                getVerticalEntry(itm as VerticalPhysicalInventoryConflictItem, entryNumber)?.quantities
                                                                    ? getVerticalEntry(itm as VerticalPhysicalInventoryConflictItem, entryNumber)?.quantities.map((qty, qtyIdx) => (
                                                                        <tr>
                                                                            <th style={{ width: '16%' }}><span>{qtyIdx + 1}</span></th>
                                                                            <td style={{ width: '42%' }}>
                                                                                <input
                                                                                    id="quantity1"
                                                                                    className="form-control"
                                                                                    name="quantity1"
                                                                                    value={qty.quantity1}
                                                                                    onChange={(e) => handleVerticalChange(e, conflictIdx, entryNumber, qtyIdx)}
                                                                                    onBlur={(e) => handleVerticalBlur(e, conflictIdx, entryNumber, qtyIdx)}
                                                                                />
                                                                            </td>
                                                                            <td style={{ width: '42%' }}>
                                                                                <input
                                                                                    id="quantity2"
                                                                                    className="form-control"
                                                                                    name="quantity2"
                                                                                    value={qty.quantity2}
                                                                                    onChange={(e) => handleVerticalChange(e, conflictIdx, entryNumber, qtyIdx)}
                                                                                    onBlur={(e) => handleVerticalBlur(e, conflictIdx, entryNumber, qtyIdx)}
                                                                                />
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                    : null
                                                            }
                                                            <tr>
                                                                <th className="table-footer" colSpan={3}><span>Total</span></th>
                                                            </tr>
                                                            <tr>
                                                                <td className="table-footer" colSpan={3}><span className="conflict-total">{getVerticalEntry(itm as VerticalPhysicalInventoryConflictItem, entryNumber)?.total}</span></td>
                                                            </tr>
                                                        </Table>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        <div className="vertical-conflict-footer">
                                            <button className="btn new attention"
                                                onClick={e => resolveVertical(itm as VerticalPhysicalInventoryConflictItem)}
                                                disabled={(itm as VerticalPhysicalInventoryConflictItem).entry1?.total != (itm as VerticalPhysicalInventoryConflictItem).entry2?.total}
                                            >RESOLVE AND SAVE</button>
                                        </div>
                                    </div>
                            }
                        </div>
                    </div>
                ))
            }
            <div className="row">
                <div className="col">
                    {getBottomButtons()}
                </div>
            </div>
        </PageBody>
    </PageContent >
});